import moment from "jalali-moment";
import { useState } from "react";

const ChatMessage = ({ message, isSender, userId, createdAt }) => {
    const isMe = isSender == userId;
    const [liked, setLiked] = useState({});
    const [bookmarked, setBookmarked] = useState({});

    const toggle = (key) => {
        if (key === "heart") setLiked((p) => ({ ...p, [message]: !p[message] }));
        if (key === "bookmark") setBookmarked((p) => ({ ...p, [message]: !p[message] }));
    };

    const buttons = [
        {
            key: "bookmark",
            activeKey: "bookmarked",
            svg: (
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17 3H7c-1.1 0-2 .9-2 2v14l7-3 7 3V5c0-1.1-.9-2-2-2z" />
                </svg>
            ),
        },
        {
            key: "edit",
            svg: (
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 32 32">
                    <path d="M 23.90625 3.96875 C 22.859375 3.96875 21.8125 4.375 21 5.1875 L 5.1875 21 L 5.125 21.3125 L 4.03125 26.8125 L 3.71875 28.28125 L 5.1875 27.96875 L 10.6875 26.875 L 11 26.8125 L 26.8125 11 C 28.4375 9.375 28.4375 6.8125 26.8125 5.1875 C 26 4.375 24.953125 3.96875 23.90625 3.96875 Z M 23.90625 5.875 C 24.410156 5.875 24.917969 6.105469 25.40625 6.59375 C 26.378906 7.566406 26.378906 8.621094 25.40625 9.59375 L 24.6875 10.28125 L 21.71875 7.3125 L 22.40625 6.59375 C 22.894531 6.105469 23.402344 5.875 23.90625 5.875 Z M 20.3125 8.71875 L 23.28125 11.6875 L 11.1875 23.78125 C 10.53125 22.5 9.5 21.46875 8.21875 20.8125 Z M 6.9375 22.4375 C 8.136719 22.921875 9.078125 23.863281 9.5625 25.0625 L 6.28125 25.71875 Z" />
                </svg>
            ),
        },
        {
            key: "heart",
            activeKey: "liked",
            svg: (
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                </svg>
            ),
        },
    ];

    const orderedButtons = isMe ? buttons : [...buttons].reverse();

    return (
        <li className={`flex ${isMe ? "justify-start" : "justify-end"} mb-1.5 group`}>
            <div className={`flex flex-col ${isMe ? "items-start" : "items-end"} max-w-[78%]`}>

                {/* ===== Bubble ===== */}
                <div
                    className={`relative inline-block px-4 py-3 rounded-2xl
                                text-[14px] leading-6 break-words whitespace-pre-wrap
                                transition-all duration-300
                                ${isMe
                            ? `bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800
                                       text-white
                                       rounded-tr-md
                                       shadow-md shadow-teal-700/25
                                       hover:shadow-lg hover:shadow-teal-600/35`
                            : `bg-white/90 dark:bg-slate-800/70
                                       text-slate-800 dark:text-slate-100
                                       rounded-tl-md
                                       ring-1 ring-slate-200/70 dark:ring-white/10
                                       shadow-sm hover:shadow-md
                                       backdrop-blur-sm`
                        }`}
                >
                    {/* Gold accent dot on the tail side */}
                    <span
                        className={`absolute top-2 w-1.5 h-1.5 rounded-full
                                    ${isMe
                                ? "left-2 bg-amber-300 shadow-[0_0_6px_rgba(251,191,36,0.9)]"
                                : "right-2 bg-amber-400 shadow-[0_0_6px_rgba(251,191,36,0.7)]"
                            }`}
                    />

                    {/* Time */}
                    <div className={`flex mb-1 ${isMe ? "justify-start" : "justify-end"}`}>
                        <span
                            className={`text-[10px] tabular-nums tracking-wide
                                        ${isMe
                                    ? "text-teal-100/80"
                                    : "text-slate-400 dark:text-slate-500"
                                }`}
                        >
                            {moment.utc(createdAt).local().locale("fa").format("HH:mm • YYYY/MM/DD")}
                        </span>
                    </div>

                    {/* Message text */}
                    <p className="font-medium">{message}</p>

                    {/* Decorative gold underline for own messages */}
                    {isMe && (
                        <span
                            className="absolute bottom-0 right-3 left-3 h-[2px] rounded-full
                                       bg-gradient-to-r from-transparent via-amber-300/60 to-transparent
                                       opacity-60"
                        />
                    )}
                </div>

                {/* ===== Actions ===== */}
                <div
                    className={`flex ${isMe ? "justify-start" : "justify-end"} gap-0.5 mt-1.5
                                opacity-0 group-hover:opacity-100
                                translate-y-[-4px] group-hover:translate-y-0
                                transition-all duration-300`}
                >
                    {orderedButtons.map(({ key, svg, activeKey }) => {
                        const isActive =
                            (key === "heart" && liked[message]) ||
                            (key === "bookmark" && bookmarked[message]);

                        return (
                            <button
                                key={key}
                                onClick={() => toggle(key)}
                                className={`h-7 w-7 rounded-lg flex items-center justify-center
                                            transition-all duration-200 active:scale-90
                                            ${isActive
                                        ? "text-amber-500 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10"
                                        : `text-slate-400 dark:text-slate-500
                                                   hover:text-teal-600 dark:hover:text-teal-400
                                                   hover:bg-teal-50 dark:hover:bg-teal-500/10`
                                    }`}
                            >
                                {svg}
                            </button>
                        );
                    })}
                </div>
            </div>
        </li>
    );
};

export default ChatMessage;